import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, Legend, CartesianGrid,
} from "recharts";

import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, IconButton, InputAdornment, MenuItem, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography, alpha,
} from "@mui/material";
import {
  Add, Delete, TrendingUp, TrendingDown, AccountBalance, CheckCircle, Payments,
} from "@mui/icons-material";

import MainContainer from "../../components/MainContainer";
import ForbiddenPage from "../../components/ForbiddenPage";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const Finance = () => {
  const { user } = useContext(AuthContext);

  const [dash, setDash] = useState(null);
  const [flow, setFlow] = useState({ entries: [], totalIncome: 0, totalExpense: 0, balance: 0 });
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ type: "expense", category: "", description: "", amount: "", status: "paid", dueDate: moment().format("YYYY-MM-DD") });

  const load = async () => {
    setLoading(true);
    try {
      const [d, f] = await Promise.all([
        api.get("/finance/dashboard"),
        api.get("/finance/cashflow", { params: filterType ? { type: filterType } : {} }),
      ]);
      setDash(d.data);
      setFlow(f.data);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user.profile === "admin") load();
    // eslint-disable-next-line
  }, [filterType]);

  const handleCreate = async () => {
    if (!form.description || !form.amount) return toast.warn("Preencha descrição e valor.");
    setSaving(true);
    try {
      await api.post("/finance/cashflow", { ...form, amount: Number(form.amount) });
      toast.success("Lançamento adicionado!");
      setOpen(false);
      setForm({ type: "expense", category: "", description: "", amount: "", status: "paid", dueDate: moment().format("YYYY-MM-DD") });
      load();
    } catch (err) { toastError(err); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir este lançamento?")) return;
    try { await api.delete(`/finance/cashflow/${id}`); toast.success("Lançamento excluído."); load(); }
    catch (err) { toastError(err); }
  };

  const handlePay = async (id) => {
    try { await api.post(`/finance/cashflow/${id}/pay`); toast.success("Marcado como pago!"); load(); }
    catch (err) { toastError(err); }
  };

  if (user.profile !== "admin") return <ForbiddenPage />;

  const cards = dash ? [
    { label: "A receber", value: dash.aReceber, icon: TrendingUp, color: "#f9a825" },
    { label: "A pagar", value: dash.aPagar, icon: TrendingDown, color: "#c62828" },
    { label: "Entradas", value: dash.entradas, icon: Payments, color: "#2e7d32" },
    { label: "Saídas", value: dash.saidas, icon: Payments, color: "#e65100" },
    { label: "Saldo", value: dash.saldo, icon: AccountBalance, color: dash.saldo >= 0 ? "#1565c0" : "#c62828" },
  ] : [];

  return (
    <MainContainer>
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 44, height: 44, borderRadius: 2.5, background: alpha("#1565c0", 0.15), display: "flex", alignItems: "center", justifyContent: "center" }}>
              <AccountBalance sx={{ color: "#1565c0" }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Financeiro</Typography>
              <Typography variant="body2" color="text.secondary">Fluxo de caixa, contas a pagar e a receber</Typography>
            </Box>
          </Stack>
          <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}>
            Novo Lançamento
          </Button>
        </Stack>

        {loading ? (
          <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress /></Box>
        ) : (
          <>
            {/* Cards */}
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              {cards.map((c, i) => (
                <Grid item xs={12} sm={6} md key={i}>
                  <Card elevation={0} sx={{ borderRadius: 3, border: `1.5px solid ${alpha(c.color, 0.25)}`, background: alpha(c.color, 0.06), height: "100%" }}>
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box sx={{ width: 40, height: 40, borderRadius: 2, background: alpha(c.color, 0.15), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <c.icon sx={{ color: c.color, fontSize: 22 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="h6" sx={{ fontWeight: 800, color: c.color, fontSize: "1.05rem", lineHeight: 1.2 }}>{brl(c.value)}</Typography>
                          <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Gráfico */}
            {dash && dash.evolucao && (
              <Card elevation={0} sx={{ borderRadius: 3, border: "1.5px solid", borderColor: "divider", mb: 3, p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Entradas x Saídas (últimos 6 meses)</Typography>
                <Box sx={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dash.evolucao} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                      <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <RTooltip formatter={(v) => brl(v)} />
                      <Legend />
                      <Bar name="Entradas" dataKey="entradas" fill="#2e7d32" radius={[4, 4, 0, 0]} />
                      <Bar name="Saídas" dataKey="saidas" fill="#c62828" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Card>
            )}

            {/* Fluxo de caixa */}
            <Card elevation={0} sx={{ borderRadius: 3, border: "1.5px solid", borderColor: "divider", overflow: "hidden" }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, flexGrow: 1 }}>Fluxo de Caixa</Typography>
                <TextField select size="small" value={filterType} onChange={(e) => setFilterType(e.target.value)} sx={{ minWidth: 140 }}>
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="income">Entradas</MenuItem>
                  <MenuItem value="expense">Saídas</MenuItem>
                </TextField>
              </Stack>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Categoria</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Valor</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Data</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {flow.entries.map((e) => (
                      <TableRow key={e.id} hover>
                        <TableCell>
                          <Chip size="small" label={e.type === "income" ? "Entrada" : "Saída"} sx={{ background: alpha(e.type === "income" ? "#2e7d32" : "#c62828", 0.15), color: e.type === "income" ? "#2e7d32" : "#c62828", fontWeight: 600 }} />
                        </TableCell>
                        <TableCell>{e.description}</TableCell>
                        <TableCell>{e.category || "-"}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: e.type === "income" ? "#2e7d32" : "#c62828" }}>
                          {e.type === "income" ? "+" : "-"} {brl(e.amount)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={e.status === "paid" ? "Pago" : "Pendente"} sx={{ background: alpha(e.status === "paid" ? "#2e7d32" : "#f9a825", 0.15), color: e.status === "paid" ? "#2e7d32" : "#854f0b", fontWeight: 600 }} />
                        </TableCell>
                        <TableCell align="center">{moment(e.paidDate || e.dueDate || e.createdAt).format("DD/MM/YYYY")}</TableCell>
                        <TableCell align="center">
                          {e.status === "pending" && (
                            <Tooltip title="Marcar como pago"><IconButton size="small" onClick={() => handlePay(e.id)}><CheckCircle fontSize="small" sx={{ color: "#2e7d32" }} /></IconButton></Tooltip>
                          )}
                          {e.source !== "charge" && (
                            <Tooltip title="Excluir"><IconButton size="small" onClick={() => handleDelete(e.id)}><Delete fontSize="small" color="error" /></IconButton></Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {flow.entries.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>Nenhum lançamento ainda.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </Box>
            </Card>
          </>
        )}
      </Box>

      {/* Dialog novo lançamento */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Novo Lançamento</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField select label="Tipo" size="small" fullWidth value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <MenuItem value="income">Entrada (receita)</MenuItem>
                <MenuItem value="expense">Saída (despesa)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Status" size="small" fullWidth value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <MenuItem value="paid">{form.type === "income" ? "Recebido" : "Pago"}</MenuItem>
                <MenuItem value="pending">{form.type === "income" ? "A receber" : "A pagar"}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={8}><TextField label="Descrição" size="small" fullWidth value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField label="Categoria" size="small" fullWidth value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Valor" size="small" fullWidth type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Data" size="small" fullWidth type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving} sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
            {saving ? <CircularProgress size={20} /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Finance;
